import React, { useContext, createContext, useMemo } from 'react'
import { getCollectionId, useSlsStateContext } from '@fs/zion-sls'
import { useFeatureFlag } from '@fs/zion-flags'
import { SPECIAL_COLLECTIONS, ALL } from './collectionConstants'

export const CollectionContext = createContext({})

/**
 * CollectionProvider component that provides collection-related context to its children.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components that will receive the context.
 * @returns {JSX.Element} The CollectionContext provider with the specified value.
 */
export function CollectionProvider({ children }) {
  const zionSlsState = useSlsStateContext()
  const isOttomanEnabled = useFeatureFlag('rip_sharedImageViewer_OttomanCensusEdit')
  const currentImage = useMemo(
    () => zionSlsState?.images?.find((img) => img.partitionKey === zionSlsState.currentImageArk),
    [zionSlsState]
  )
  const collectionId = useMemo(() => currentImage && getCollectionId(currentImage), [currentImage])
  // if an image is deprecated restrict all fields
  const restrictAllFields = currentImage?.deprecatedImage ? ALL : null
  const restrictedFields = isOttomanEnabled.isOn
    ? []
    : restrictAllFields || SPECIAL_COLLECTIONS[collectionId]?.restrictedFields || [] // white list of fields that are editable
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  return <CollectionContext.Provider value={{ collectionId, restrictedFields }}>{children}</CollectionContext.Provider>
}

/**
 * Custom hook to use the CollectionContext.
 *
 * @returns {Object} The context value containing collectionId, restrictedFields, and limited.
 */
export const useCollectionContextProvider = () => {
  return useContext(CollectionContext)
}

export default CollectionProvider
